import { NavigableModal } from '$components'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { UpdatePetInformationForm, UpdatePetInformationSchema, Input } from '$features/pets/forms/update-pet-information/index'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { petShowOptions, petShowQueryKey, petUpdateMutation } from '$api-client/@tanstack/react-query.gen'
import { DogBreed, Gender } from '$api-client/types.gen'
import { ToastHelper } from '$components'
import { PetsFilterParam } from '$features/pets/enums/filter-params'

export function EditPetInformationModal() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const { data } = useQuery({
    ...petShowOptions({
      path: {
        pet: Number(id),
      },
      query: {
        include_deleted: true,
        [PetsFilterParam.IncludeUnapproved]: searchParams.get(PetsFilterParam.IncludeUnapproved) === '1',
      },
    }),
  })

  const { mutate: updatePet, isPending: isUpdatingPet } = useMutation({
    ...petUpdateMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: petShowQueryKey({
          path: {
            pet: Number(id),
          },
        }),
      })

      ToastHelper.success('Pet information updated successfully.')
      handleClose()
    },
    onError: () => ToastHelper.error('Failed to update pet information.'),
  })

  const handleClose = () => navigate(-1)

  const handleSubmit = async (values: UpdatePetInformationSchema) => {
    updatePet({
      path: {
        pet: Number(id),
      },
      body: {
        birth_date: values[Input.BirthDate],
        breed: values[Input.Breed].id as DogBreed,
        gender: values[Input.Gender] as Gender,
        size_category_id: values[Input.SizeCategory]?.id ? Number(values[Input.SizeCategory].id) : undefined,
        coat_type_id: values[Input.CoatType]?.id ? Number(values[Input.CoatType].id) : undefined,
      },
    })
  }

  const pet = data?.data

  if (!pet) {
    return null
  }

  return (
    <UpdatePetInformationForm
      onSubmit={handleSubmit}
      onCancel={handleClose}
      isLoading={isUpdatingPet}
      defaultValues={{
        [Input.Breed]: {
          id: pet.breed[0],
          value: pet.breed[0],
        },
        [Input.Gender]: pet.gender ?? '',
        [Input.BirthDate]: pet.birth_date ?? undefined,
        [Input.SizeCategory]: pet.size_category ? { id: String(pet.size_category.id), value: pet.size_category.values?.name ?? String(pet.size_category.id) } : undefined,
        [Input.CoatType]: pet.coat_type ? { id: String(pet.coat_type.id), value: pet.coat_type.values?.name ?? String(pet.coat_type.id) } : undefined,
      }}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Edit Pet Information
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <UpdatePetInformationForm.Content />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <UpdatePetInformationForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </UpdatePetInformationForm>
  )
}
